'use client';
import React, { useState } from 'react';
import { BookOpen, CheckCircle, AlertTriangle, Lock, ChevronDown, ChevronUp, Download, RefreshCw, Search, ArrowUpRight, ArrowDownLeft, Shield, Link2, Hash, Eye, XCircle, Clock } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';

type EntryType = 'Debit' | 'Credit';
type ReconciliationStatus = 'Reconciled' | 'Unreconciled' | 'Disputed' | 'Pending';
type LedgerAccount = 'Escrow — Client Funds' | 'Investment Receipts' | 'Settlement Payable' | 'Platform Fee Revenue' | 'Investor Distribution Payable' | 'Bank — Barclays Operating' | 'Suspense Account';

const reconBg = (s: ReconciliationStatus) => {
  if (s === 'Reconciled') return 'bg-positive/10 text-positive border-positive/30';
  if (s === 'Unreconciled') return 'bg-critical/10 text-critical border-critical/30';
  if (s === 'Disputed') return 'bg-warning/10 text-warning border-warning/30';
  return 'bg-info/10 text-info border-info/30';
};

const reconIcon = (s: ReconciliationStatus) => {
  if (s === 'Reconciled') return CheckCircle;
  if (s === 'Unreconciled') return XCircle;
  if (s === 'Disputed') return AlertTriangle;
  return Clock;
};

interface LedgerEntry {
  id: string;
  journalRef: string;
  date: string;
  description: string;
  account: LedgerAccount;
  counterAccount: LedgerAccount;
  entryType: EntryType;
  amount: number;
  currency: string;
  txnRef: string | null;
  campaignRef: string | null;
  investorRef: string | null;
  reconStatus: ReconciliationStatus;
  integrityHash: string;
  prevHash: string;
  blockSeq: number;
  postedBy: string;
  approvedBy: string | null;
  notes: string;
}

const ledgerEntries: LedgerEntry[] = [
  {
    id: 'JNL-2026-0841',
    journalRef: 'JNL-2026-0841',
    date: '2026-08-20 14:22:07',
    description: 'Investment receipt — Thornfield Capital Holdings Ltd — Meridian Logistics Hub Series B',
    account: 'Escrow — Client Funds',
    counterAccount: 'Investment Receipts',
    entryType: 'Debit',
    amount: 2850000,
    currency: 'GBP',
    txnRef: 'TXN-884921',
    campaignRef: 'CMP-001',
    investorRef: 'INV-001',
    reconStatus: 'Pending',
    integrityHash: 'sha256:a3f8c2d1e4b7f9a0c5d2e8f1b4a7c0d3e6f9a2b5c8d1e4f7a0b3c6d9e2f5a8b1',
    prevHash: 'sha256:9e2f5a8b1c4d7e0f3a6b9c2d5e8f1a4b7c0d3e6f9a2b5c8d1e4f7a0b3c6d9e2',
    blockSeq: 841,
    postedBy: 'System — Auto',
    approvedBy: null,
    notes: 'Awaiting AML clearance before settlement. Funds held in escrow.',
  },
  {
    id: 'JNL-2026-0840',
    journalRef: 'JNL-2026-0840',
    date: '2026-08-20 11:07:33',
    description: 'Investment receipt BLOCKED — Unknown Beneficial Owner Entity — Ashford Commercial REIT II',
    account: 'Suspense Account',
    counterAccount: 'Investment Receipts',
    entryType: 'Debit',
    amount: 480000,
    currency: 'GBP',
    txnRef: 'TXN-883104',
    campaignRef: 'CMP-002',
    investorRef: 'INV-005',
    reconStatus: 'Disputed',
    integrityHash: 'sha256:b4c7d0e3f6a9b2c5d8e1f4a7b0c3d6e9f2a5b8c1d4e7f0a3b6c9d2e5f8a1b4c7',
    prevHash: 'sha256:a3f8c2d1e4b7f9a0c5d2e8f1b4a7c0d3e6f9a2b5c8d1e4f7a0b3c6d9e2f5a8b1',
    blockSeq: 840,
    postedBy: 'System — Auto',
    approvedBy: null,
    notes: 'Funds moved to suspense pending MLRO investigation. SAR filed.',
  },
  {
    id: 'JNL-2026-0839',
    journalRef: 'JNL-2026-0839',
    date: '2026-08-19 16:44:12',
    description: 'Investment receipt — Crestwood Family Office LLC — Meridian Logistics Hub Series B',
    account: 'Escrow — Client Funds',
    counterAccount: 'Investment Receipts',
    entryType: 'Debit',
    amount: 1200000,
    currency: 'GBP',
    txnRef: 'TXN-881577',
    campaignRef: 'CMP-001',
    investorRef: 'INV-002',
    reconStatus: 'Reconciled',
    integrityHash: 'sha256:c5d8e1f4a7b0c3d6e9f2a5b8c1d4e7f0a3b6c9d2e5f8a1b4c7d0e3f6a9b2c5d8',
    prevHash: 'sha256:b4c7d0e3f6a9b2c5d8e1f4a7b0c3d6e9f2a5b8c1d4e7f0a3b6c9d2e5f8a1b4c7',
    blockSeq: 839,
    postedBy: 'System — Auto',
    approvedBy: 'F. Williams',
    notes: 'Reconciled against bank statement ref BS-20260819-044.',
  },
  {
    id: 'JNL-2026-0838',
    journalRef: 'JNL-2026-0838',
    date: '2026-08-19 16:44:12',
    description: 'Investment Receipts contra — Crestwood Family Office LLC',
    account: 'Investment Receipts',
    counterAccount: 'Escrow — Client Funds',
    entryType: 'Credit',
    amount: 1200000,
    currency: 'GBP',
    txnRef: 'TXN-881577',
    campaignRef: 'CMP-001',
    investorRef: 'INV-002',
    reconStatus: 'Reconciled',
    integrityHash: 'sha256:d6e9f2a5b8c1d4e7f0a3b6c9d2e5f8a1b4c7d0e3f6a9b2c5d8e1f4a7b0c3d6e9',
    prevHash: 'sha256:c5d8e1f4a7b0c3d6e9f2a5b8c1d4e7f0a3b6c9d2e5f8a1b4c7d0e3f6a9b2c5d8',
    blockSeq: 838,
    postedBy: 'System — Auto',
    approvedBy: 'F. Williams',
    notes: 'Double-entry contra for JNL-2026-0839.',
  },
  {
    id: 'JNL-2026-0837',
    journalRef: 'JNL-2026-0837',
    date: '2026-08-18 09:15:44',
    description: 'Settlement disbursement — Ashford Pension Trustees — Greenfield Solar Farm Fund',
    account: 'Settlement Payable',
    counterAccount: 'Escrow — Client Funds',
    entryType: 'Credit',
    amount: 150000,
    currency: 'GBP',
    txnRef: 'TXN-879002',
    campaignRef: 'CMP-003',
    investorRef: 'INV-003',
    reconStatus: 'Reconciled',
    integrityHash: 'sha256:e7f0a3b6c9d2e5f8a1b4c7d0e3f6a9b2c5d8e1f4a7b0c3d6e9f2a5b8c1d4e7f0',
    prevHash: 'sha256:d6e9f2a5b8c1d4e7f0a3b6c9d2e5f8a1b4c7d0e3f6a9b2c5d8e1f4a7b0c3d6e9',
    blockSeq: 837,
    postedBy: 'F. Williams',
    approvedBy: 'R. Osei',
    notes: 'Settlement processed. Bank confirmation ref BACS-20260818-0091.',
  },
  {
    id: 'JNL-2026-0836',
    journalRef: 'JNL-2026-0836',
    date: '2026-08-18 09:15:44',
    description: 'Platform fee accrual — Greenfield Solar Farm Fund — 1.5% platform fee',
    account: 'Platform Fee Revenue',
    counterAccount: 'Escrow — Client Funds',
    entryType: 'Credit',
    amount: 2250,
    currency: 'GBP',
    txnRef: 'TXN-879002',
    campaignRef: 'CMP-003',
    investorRef: null,
    reconStatus: 'Reconciled',
    integrityHash: 'sha256:f8a1b4c7d0e3f6a9b2c5d8e1f4a7b0c3d6e9f2a5b8c1d4e7f0a3b6c9d2e5f8a1',
    prevHash: 'sha256:e7f0a3b6c9d2e5f8a1b4c7d0e3f6a9b2c5d8e1f4a7b0c3d6e9f2a5b8c1d4e7f0',
    blockSeq: 836,
    postedBy: 'System — Auto',
    approvedBy: 'F. Williams',
    notes: '1.5% platform fee on £150,000 settlement.',
  },
  {
    id: 'JNL-2026-0835',
    journalRef: 'JNL-2026-0835',
    date: '2026-08-17 14:00:00',
    description: 'Investor distribution — Q2 2026 — Meridian Sovereign Fund',
    account: 'Investor Distribution Payable',
    counterAccount: 'Bank — Barclays Operating',
    entryType: 'Debit',
    amount: 48750,
    currency: 'GBP',
    txnRef: null,
    campaignRef: 'CMP-001',
    investorRef: 'INV-004',
    reconStatus: 'Unreconciled',
    integrityHash: 'sha256:a9b2c5d8e1f4a7b0c3d6e9f2a5b8c1d4e7f0a3b6c9d2e5f8a1b4c7d0e3f6a9b2',
    prevHash: 'sha256:f8a1b4c7d0e3f6a9b2c5d8e1f4a7b0c3d6e9f2a5b8c1d4e7f0a3b6c9d2e5f8a1',
    blockSeq: 835,
    postedBy: 'F. Williams',
    approvedBy: 'R. Osei',
    notes: 'Distribution dispatched. Bank confirmation pending. Expected 2026-08-19.',
  },
];

// Account balances
const accountBalances = [
  { account: 'Escrow — Client Funds', openingBalance: 18420000, debits: 4050000, credits: 150000, closingBalance: 22320000, currency: 'GBP' },
  { account: 'Investment Receipts', openingBalance: 0, debits: 0, credits: 4530000, closingBalance: 4530000, currency: 'GBP' },
  { account: 'Settlement Payable', openingBalance: 320000, debits: 0, credits: 150000, closingBalance: 470000, currency: 'GBP' },
  { account: 'Platform Fee Revenue', openingBalance: 142800, debits: 0, credits: 2250, closingBalance: 145050, currency: 'GBP' },
  { account: 'Investor Distribution Payable', openingBalance: 0, debits: 48750, credits: 0, closingBalance: 48750, currency: 'GBP' },
  { account: 'Bank — Barclays Operating', openingBalance: 2100000, debits: 0, credits: 48750, closingBalance: 2148750, currency: 'GBP' },
  { account: 'Suspense Account', openingBalance: 0, debits: 480000, credits: 0, closingBalance: 480000, currency: 'GBP' },
];

const fmt = (n: number) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(n);
const shortHash = (h: string) => h.substring(0, 20) + '…' + h.substring(h.length - 8);

type ViewMode = 'journal' | 'balances';

export default function LedgerViewer() {
  const [viewMode, setViewMode] = useState<ViewMode>('journal');
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRecon, setFilterRecon] = useState<ReconciliationStatus | 'All'>('All');
  const [filterType, setFilterType] = useState<EntryType | 'All'>('All');

  const filtered = ledgerEntries.filter(e => {
    if (filterRecon !== 'All' && e.reconStatus !== filterRecon) return false;
    if (filterType !== 'All' && e.entryType !== filterType) return false;
    if (searchQuery && !e.description.toLowerCase().includes(searchQuery.toLowerCase()) && !e.journalRef.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const totalDebits = ledgerEntries.filter(e => e.entryType === 'Debit').reduce((s, e) => s + e.amount, 0);
  const totalCredits = ledgerEntries.filter(e => e.entryType === 'Credit').reduce((s, e) => s + e.amount, 0);
  const reconciledCount = ledgerEntries.filter(e => e.reconStatus === 'Reconciled').length;
  const unreconciledCount = ledgerEntries.filter(e => e.reconStatus === 'Unreconciled' || e.reconStatus === 'Disputed').length;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-surface-1">
        <div>
          <h1 className="text-xl font-bold text-foreground tracking-tight">Double-Entry Ledger</h1>
          <p className="text-sm text-foreground-muted mt-0.5">Journal entries · Debit/credit pairs · Reconciliation status · Cryptographic integrity proofs</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-sm flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
          <button className="btn-secondary text-sm flex items-center gap-1.5"><Download size={14} /> Export</button>
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 px-6 py-4 border-b border-border bg-surface-1">
        <MetricCard label="Total Debits (Period)" value="£4.53M" trend="neutral" trendValue="8 entries" />
        <MetricCard label="Total Credits (Period)" value="£0.20M" trend="neutral" trendValue="3 entries" />
        <MetricCard label="Reconciled Entries" value={`${reconciledCount}/${ledgerEntries.length}`} trend="neutral" trendValue="Matched to bank" variant="positive" />
        <MetricCard label="Unreconciled / Disputed" value={String(unreconciledCount)} trend="up" trendValue="Requires attention" variant={unreconciledCount > 0 ? 'critical' : 'positive'} />
      </div>

      {/* View Toggle + Filters */}
      <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-border bg-surface-1">
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button
            onClick={() => setViewMode('journal')}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === 'journal' ? 'bg-accent text-white' : 'bg-surface-1 text-foreground-muted hover:text-foreground'}`}
          >
            Journal Entries
          </button>
          <button
            onClick={() => setViewMode('balances')}
            className={`px-3 py-1.5 text-sm font-medium transition-colors ${viewMode === 'balances' ? 'bg-accent text-white' : 'bg-surface-1 text-foreground-muted hover:text-foreground'}`}
          >
            Account Balances
          </button>
        </div>

        {viewMode === 'journal' && (
          <>
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-muted" />
              <input
                type="text"
                placeholder="Search journal entries..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="input pl-8 py-1.5 text-sm w-full"
              />
            </div>
            <select value={filterType} onChange={e => setFilterType(e.target.value as EntryType | 'All')} className="input py-1.5 text-sm">
              <option value="All">All Entry Types</option>
              <option value="Debit">Debit</option>
              <option value="Credit">Credit</option>
            </select>
            <select value={filterRecon} onChange={e => setFilterRecon(e.target.value as ReconciliationStatus | 'All')} className="input py-1.5 text-sm">
              <option value="All">All Reconciliation</option>
              <option value="Reconciled">Reconciled</option>
              <option value="Unreconciled">Unreconciled</option>
              <option value="Disputed">Disputed</option>
              <option value="Pending">Pending</option>
            </select>
            <span className="text-xs text-foreground-muted ml-auto">{filtered.length} entries</span>
          </>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">

        {/* ── Journal Entries View ── */}
        {viewMode === 'journal' && (
          <div className="space-y-2">
            {filtered.map(entry => {
              const ReconIcon = reconIcon(entry.reconStatus);
              const isExpanded = expandedEntry === entry.id;
              return (
                <div key={entry.id} className={`card overflow-hidden border ${entry.reconStatus === 'Unreconciled' ? 'border-critical/20' : entry.reconStatus === 'Disputed' ? 'border-warning/20' : 'border-border'}`}>
                  {/* Entry Row */}
                  <button
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-2/40 transition-colors text-left"
                    onClick={() => setExpandedEntry(isExpanded ? null : entry.id)}
                  >
                    {/* Entry type indicator */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${entry.entryType === 'Debit' ? 'bg-accent/10 text-accent' : 'bg-positive/10 text-positive'}`}>
                      {entry.entryType === 'Debit' ? <ArrowDownLeft size={15} /> : <ArrowUpRight size={15} />}
                    </div>

                    {/* Journal ref + description */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-mono-data text-foreground-muted">{entry.journalRef}</span>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${entry.entryType === 'Debit' ? 'bg-accent/10 text-accent' : 'bg-positive/10 text-positive'}`}>
                          {entry.entryType}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border flex items-center gap-1 ${reconBg(entry.reconStatus)}`}>
                          <ReconIcon size={10} />
                          {entry.reconStatus}
                        </span>
                      </div>
                      <p className="text-sm text-foreground truncate">{entry.description}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <span className="text-xs text-foreground-muted">{entry.account}</span>
                        <span className="text-xs text-foreground-muted">→</span>
                        <span className="text-xs text-foreground-muted">{entry.counterAccount}</span>
                      </div>
                    </div>

                    {/* Amount + date */}
                    <div className="text-right flex-shrink-0">
                      <div className={`text-sm font-bold font-mono-data ${entry.entryType === 'Debit' ? 'text-accent' : 'text-positive'}`}>
                        {entry.entryType === 'Debit' ? 'Dr ' : 'Cr '}{fmt(entry.amount)}
                      </div>
                      <div className="text-xs text-foreground-muted font-mono-data">{entry.date.split(' ')[0]}</div>
                    </div>

                    {/* Integrity indicator */}
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <Lock size={12} className="text-positive opacity-60" />
                      {isExpanded ? <ChevronUp size={14} className="text-foreground-muted" /> : <ChevronDown size={14} className="text-foreground-muted" />}
                    </div>
                  </button>

                  {/* Expanded Detail */}
                  {isExpanded && (
                    <div className="border-t border-border bg-surface-2/20 px-4 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {/* Journal Details */}
                        <div>
                          <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-3">Journal Details</p>
                          <div className="space-y-2">
                            <div className="flex justify-between text-xs">
                              <span className="text-foreground-muted">Entry Type</span>
                              <span className={`font-semibold ${entry.entryType === 'Debit' ? 'text-accent' : 'text-positive'}`}>{entry.entryType}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-foreground-muted">Debit Account</span>
                              <span className="text-foreground text-right max-w-[160px]">{entry.entryType === 'Debit' ? entry.account : entry.counterAccount}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-foreground-muted">Credit Account</span>
                              <span className="text-foreground text-right max-w-[160px]">{entry.entryType === 'Credit' ? entry.account : entry.counterAccount}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-foreground-muted">Amount</span>
                              <span className="font-mono-data font-bold text-foreground">{fmt(entry.amount)} {entry.currency}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-foreground-muted">Posted At</span>
                              <span className="font-mono-data text-foreground">{entry.date}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-foreground-muted">Posted By</span>
                              <span className="text-foreground">{entry.postedBy}</span>
                            </div>
                            <div className="flex justify-between text-xs">
                              <span className="text-foreground-muted">Approved By</span>
                              <span className="text-foreground">{entry.approvedBy ?? '—'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Transaction Linkage */}
                        <div>
                          <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-3">Transaction Linkage</p>
                          <div className="space-y-2">
                            {entry.txnRef && (
                              <div className="flex items-center gap-2 p-2 rounded bg-surface-2 border border-border">
                                <Link2 size={12} className="text-accent flex-shrink-0" />
                                <div>
                                  <span className="text-xs text-foreground-muted block">Transaction</span>
                                  <span className="text-xs font-mono-data text-accent">{entry.txnRef}</span>
                                </div>
                              </div>
                            )}
                            {entry.campaignRef && (
                              <div className="flex items-center gap-2 p-2 rounded bg-surface-2 border border-border">
                                <Link2 size={12} className="text-foreground-muted flex-shrink-0" />
                                <div>
                                  <span className="text-xs text-foreground-muted block">Campaign</span>
                                  <span className="text-xs font-mono-data text-foreground">{entry.campaignRef}</span>
                                </div>
                              </div>
                            )}
                            {entry.investorRef && (
                              <div className="flex items-center gap-2 p-2 rounded bg-surface-2 border border-border">
                                <Link2 size={12} className="text-foreground-muted flex-shrink-0" />
                                <div>
                                  <span className="text-xs text-foreground-muted block">Investor</span>
                                  <span className="text-xs font-mono-data text-foreground">{entry.investorRef}</span>
                                </div>
                              </div>
                            )}
                            {!entry.txnRef && !entry.campaignRef && !entry.investorRef && (
                              <span className="text-xs text-foreground-muted">No linked references</span>
                            )}
                            <div className="mt-2">
                              <span className="text-xs text-foreground-muted block mb-1">Notes</span>
                              <p className="text-xs text-foreground bg-surface-2 rounded p-2 border border-border leading-relaxed">{entry.notes}</p>
                            </div>
                          </div>
                        </div>

                        {/* Cryptographic Integrity */}
                        <div>
                          <p className="text-xs font-semibold text-foreground-muted uppercase tracking-wider mb-3">Cryptographic Integrity Proof</p>
                          <div className="space-y-3">
                            <div className="p-3 rounded-lg bg-surface-2 border border-border">
                              <div className="flex items-center gap-1.5 mb-2">
                                <Hash size={12} className="text-positive" />
                                <span className="text-xs font-semibold text-foreground-muted">Entry Hash (SHA-256)</span>
                              </div>
                              <p className="text-xs font-mono-data text-positive break-all leading-relaxed">{shortHash(entry.integrityHash)}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-surface-2 border border-border">
                              <div className="flex items-center gap-1.5 mb-2">
                                <Hash size={12} className="text-foreground-muted" />
                                <span className="text-xs font-semibold text-foreground-muted">Previous Hash</span>
                              </div>
                              <p className="text-xs font-mono-data text-foreground-muted break-all leading-relaxed">{shortHash(entry.prevHash)}</p>
                            </div>
                            <div className="flex items-center justify-between p-2 rounded bg-positive/5 border border-positive/20">
                              <div className="flex items-center gap-1.5">
                                <Shield size={12} className="text-positive" />
                                <span className="text-xs text-positive font-medium">Chain Integrity Verified</span>
                              </div>
                              <span className="text-xs font-mono-data text-foreground-muted">Block #{entry.blockSeq}</span>
                            </div>
                            <div className="flex gap-2">
                              <button className="btn-ghost text-xs flex items-center gap-1"><Eye size={11} /> Verify Hash</button>
                              <button className="btn-ghost text-xs flex items-center gap-1"><Download size={11} /> Proof</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <BookOpen size={40} className="text-foreground-muted mb-3 opacity-40" />
                <p className="text-foreground-muted text-sm">No journal entries match the current filters</p>
              </div>
            )}
          </div>
        )}

        {/* ── Account Balances View ── */}
        {viewMode === 'balances' && (
          <div className="space-y-4">
            <div className="card overflow-hidden">
              <div className="px-5 py-3 border-b border-border bg-surface-2/50 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">Trial Balance — Period End 2026-08-20</h2>
                <div className="flex items-center gap-2">
                  <CheckCircle size={14} className="text-positive" />
                  <span className="text-xs text-positive font-medium">Balanced</span>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-surface-2">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-foreground-muted uppercase tracking-wider">Account</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-muted uppercase tracking-wider">Opening Balance</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-muted uppercase tracking-wider">Debits</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-muted uppercase tracking-wider">Credits</th>
                    <th className="text-right px-5 py-3 text-xs font-semibold text-foreground-muted uppercase tracking-wider">Closing Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {accountBalances.map((acct, idx) => (
                    <tr key={acct.account} className={`border-b border-border hover:bg-surface-2/40 transition-colors ${idx % 2 === 0 ? '' : 'bg-surface-2/10'}`}>
                      <td className="px-5 py-3 font-medium text-foreground text-sm">{acct.account}</td>
                      <td className="px-5 py-3 text-right font-mono-data text-sm text-foreground-muted">{fmt(acct.openingBalance)}</td>
                      <td className="px-5 py-3 text-right font-mono-data text-sm text-accent">{acct.debits > 0 ? fmt(acct.debits) : '—'}</td>
                      <td className="px-5 py-3 text-right font-mono-data text-sm text-positive">{acct.credits > 0 ? fmt(acct.credits) : '—'}</td>
                      <td className="px-5 py-3 text-right font-mono-data text-sm font-bold text-foreground">{fmt(acct.closingBalance)}</td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr className="border-t-2 border-border bg-surface-2/30">
                    <td className="px-5 py-3 font-bold text-foreground text-sm">TOTALS</td>
                    <td className="px-5 py-3 text-right font-mono-data text-sm font-bold text-foreground">
                      {fmt(accountBalances.reduce((s, a) => s + a.openingBalance, 0))}
                    </td>
                    <td className="px-5 py-3 text-right font-mono-data text-sm font-bold text-accent">
                      {fmt(accountBalances.reduce((s, a) => s + a.debits, 0))}
                    </td>
                    <td className="px-5 py-3 text-right font-mono-data text-sm font-bold text-positive">
                      {fmt(accountBalances.reduce((s, a) => s + a.credits, 0))}
                    </td>
                    <td className="px-5 py-3 text-right font-mono-data text-sm font-bold text-foreground">
                      {fmt(accountBalances.reduce((s, a) => s + a.closingBalance, 0))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Reconciliation Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle size={15} className="text-positive" />
                  <span className="text-sm font-semibold text-foreground">Reconciled</span>
                  <span className="ml-auto text-xs font-mono-data text-positive bg-positive/10 px-2 py-0.5 rounded-full">{reconciledCount}</span>
                </div>
                <p className="text-xs text-foreground-muted">Entries matched to bank statements and confirmed by finance officer.</p>
              </div>
              <div className="card p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock size={15} className="text-info" />
                  <span className="text-sm font-semibold text-foreground">Pending</span>
                  <span className="ml-auto text-xs font-mono-data text-info bg-info/10 px-2 py-0.5 rounded-full">{ledgerEntries.filter(e => e.reconStatus === 'Pending').length}</span>
                </div>
                <p className="text-xs text-foreground-muted">Awaiting bank confirmation or compliance clearance before reconciliation.</p>
              </div>
              <div className="card p-4">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle size={15} className="text-critical" />
                  <span className="text-sm font-semibold text-foreground">Unreconciled / Disputed</span>
                  <span className="ml-auto text-xs font-mono-data text-critical bg-critical/10 px-2 py-0.5 rounded-full">{unreconciledCount}</span>
                </div>
                <p className="text-xs text-foreground-muted">Discrepancies or blocked entries requiring finance officer investigation.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
